/**
 * `Popover` (W1-12 ui-kit-i18n; contract PopoverProps): Radix popover for small forms and pickers. Controlled
 * when `open` is given, otherwise it manages its own state. The trigger element receives aria-expanded /
 * aria-controls and must accept a ref and DOM props (Button, IconButton and native elements do); other content
 * is wrapped in a button. A Button / IconButton trigger with `command` toggles the popover and still runs the
 * command (internal/commandButton.ts). Focus moves into the content on open and back to the trigger on close;
 * Escape and clicks outside close it (an Escape that reverts a field's typed text stays with the field).
 */

import * as RP from '@radix-ui/react-popover';
import { isValidElement, type ReactNode } from 'react';
import { asTriggerChild } from './internal/commandButton';
import { cx } from './internal/cx';
import { keepFocusOnPointerDown } from './internal/focus';
import { keepOpenForPendingEdit } from './internal/layers';
import s from './Popover.module.css';
import type { PopoverProps } from './types';

export interface PopoverExtraProps {
  /** Accessible name of the content (dialog role) when it has no visible heading. */
  readonly label?: string | undefined;
  /** Extra class for the content surface (width of pickers). */
  readonly contentClassName?: string | undefined;
  /** Called when focus would move into the content on open; preventDefault keeps it on the trigger. */
  onOpenAutoFocus?(e: Event): void;
}

function triggerElement(trigger: ReactNode): ReactNode {
  if (isValidElement(trigger)) return asTriggerChild(trigger);
  return (
    <button type="button" className={s.textTrigger} onMouseDown={keepFocusOnPointerDown}>
      {trigger}
    </button>
  );
}

export function Popover(props: PopoverProps & PopoverExtraProps) {
  const rootProps = {
    ...(props.open === undefined ? {} : { open: props.open }),
    ...(props.onOpenChange === undefined
      ? {}
      : {
          onOpenChange: (open: boolean) => {
            props.onOpenChange?.(open);
          },
        }),
  };
  return (
    <RP.Root {...rootProps}>
      <RP.Trigger asChild>{triggerElement(props.trigger)}</RP.Trigger>
      <RP.Portal>
        <RP.Content
          className={cx(s.content, props.contentClassName)}
          side={props.side ?? 'bottom'}
          align={props.align ?? 'center'}
          sideOffset={6}
          collisionPadding={8}
          aria-label={props.label}
          onOpenAutoFocus={(e) => {
            props.onOpenAutoFocus?.(e);
          }}
          onEscapeKeyDown={keepOpenForPendingEdit}
        >
          {props.children}
        </RP.Content>
      </RP.Portal>
    </RP.Root>
  );
}
